// src/scenes/sitemanager/SiteReportCard.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../component-files/Header";
import { useAuth } from "../../context/AuthContext";

export default function SiteReportCard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth();

    // filters
    const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
    const [monthFilter, setMonthFilter] = useState("");

    // raw data
    const [incData, setIncData] = useState([]);
    const [expData, setExpData] = useState([]);
    const [mplhData, setMplhData] = useState([]);

    // dropdown options
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    useEffect(() => {
        Promise.all([
            axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_IncomePerMEQBySiteMonth"),
            axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_ExpensePerMEQBySiteMonth"),
            axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_MealPerLaborHourBySiteMonth"),
        ]).then(([incRes, expRes, mplhRes]) => {
            const inc = incRes.data;
            const exp = expRes.data;
            const mplh = mplhRes.data;

            setIncData(inc);
            setExpData(exp);
            setMplhData(mplh);

            // build year/month lists
            const ySet = new Set(), mSet = new Set();
            inc.forEach(r => {
                const [y, m] = r.YearMonth.split("-");
                ySet.add(y);
                mSet.add(r.YearMonth);
            });
            setYears(Array.from(ySet).sort());
            setMonths(Array.from(mSet).sort());
        });
    }, [allocations, isSchoolUser]);

    // filter by year/month
    const incFy = useMemo(
        () => incData.filter(r =>
            r.YearMonth.startsWith(yearFilter) && (!monthFilter || r.YearMonth === monthFilter)
        ),
        [incData, yearFilter, monthFilter]
    );
    const expFy = useMemo(
        () => expData.filter(r =>
            r.YearMonth.startsWith(yearFilter) && (!monthFilter || r.YearMonth === monthFilter)
        ),
        [expData, yearFilter, monthFilter]
    );
    const mplhFy = useMemo(
        () => mplhData.filter(r =>
            r.YearMonth.startsWith(yearFilter) && (!monthFilter || r.YearMonth === monthFilter)
        ),
        [mplhData, yearFilter, monthFilter]
    );

    // build per-site lookup
    const sites = useMemo(() => {
        const map = {};
        incFy.concat(expFy).concat(mplhFy).forEach(r => {
            map[r.SchoolId] = r.SchoolName;
        });
        return Object.entries(map).map(([id, name]) => ({ id, name }));
    }, [incFy, expFy, mplhFy]);

    // compute rankings
    const metricRanks = useMemo(() => {
        const out = {}; // id -> { inc,val,incRank,exp,val,expRank,mplh,val,mplhRank }
        const N = sites.length;

        // income/MEQ ranking
        const incArr = sites.map(s => {
            const tot = incFy.filter(r => r.SchoolId === s.id).reduce((a, r) => a + r.IncomePerMEQ, 0);
            return { id: s.id, val: tot };
        }).sort((a, b) => b.val - a.val);
        incArr.forEach((d, i) => out[d.id] = { ...(out[d.id] || {}), inc: d.val, incRank: i + 1 });

        // expense/MEQ ranking
        const expArr = sites.map(s => {
            const tot = expFy.filter(r => r.SchoolId === s.id).reduce((a, r) => a + r.ExpensePerMEQ, 0);
            return { id: s.id, val: tot };
        }).sort((a, b) => b.val - a.val);
        expArr.forEach((d, i) => out[d.id] = { ...out[d.id], exp: d.val, expRank: i + 1 });

        // MPLH ranking
        const mplhArr = sites.map(s => {
            const tot = mplhFy.filter(r => r.SchoolId === s.id).reduce((a, r) => a + r.MPLH, 0);
            return { id: s.id, val: tot };
        }).sort((a, b) => b.val - a.val);
        mplhArr.forEach((d, i) => out[d.id] = { ...out[d.id], mplh: d.val, mplhRank: i + 1 });

        // combined score
        sites.forEach(s => {
            const m = out[s.id];
            const sInc = (N - m.incRank + 1) / N;
            const sExp = (N - m.expRank + 1) / N;
            const sMplh = (N - m.mplhRank + 1) / N;
            m.combined = Math.round(((sInc + sExp + sMplh) / 3) * 100);
        });

        return out;
    }, [sites, incFy, expFy, mplhFy]);

    // prepare rows
    const rows = useMemo(() => sites.map((s, i) => {
        const m = metricRanks[s.id];
        return {
            id: s.id,
            site: s.name,
            incomePerMEQ: m.inc,
            incomeRank: m.incRank,
            expensePerMEQ: m.exp,
            expenseRank: m.expRank,
            mplh: m.mplh,
            mplhRank: m.mplhRank,
            score: m.combined
        };
    }), [sites, metricRanks]);

    const columns = [
        { field: "site", headerName: "Site", flex: 1 },
        { field: "incomePerMEQ", headerName: "Income/MEQ", type: "number", flex: 1, valueFormatter: ({ value }) => value.toFixed(2) },
        { field: "incomeRank", headerName: "Inc Rank", type: "number", width: 100 },
        { field: "expensePerMEQ", headerName: "Expense/MEQ", type: "number", flex: 1, valueFormatter: ({ value }) => value.toFixed(2) },
        { field: "expenseRank", headerName: "Exp Rank", type: "number", width: 100 },
        { field: "mplh", headerName: "MPLH", type: "number", flex: 1, valueFormatter: ({ value }) => value.toFixed(2) },
        { field: "mplhRank", headerName: "MPLH Rank", type: "number", width: 100 },
        { field: "score", headerName: "Score (%)", type: "number", flex: 1 },
    ];

    // download CSV
    const downloadCsv = () => {
        const header = ["Site", "Income/MEQ", "Inc Rank", "Expense/MEQ", "Exp Rank", "MPLH", "MPLH Rank", "Score"];
        const csv = [
            header.join(","),
            ...rows.map(r => [
                r.site,
                r.incomePerMEQ.toFixed(2),
                r.incomeRank,
                r.expensePerMEQ.toFixed(2),
                r.expenseRank,
                r.mplh.toFixed(2),
                r.mplhRank,
                r.score
            ].join(","))
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "SiteReportCard.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box p={3}>
            <Header title="Site Report Card" subtitle="Combined ranking across three metrics" />

            {/* Filters */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                        {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {months.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={downloadCsv} sx={{ ml: "auto" }}>
                    Download CSV
                </Button>
            </Box>

            {/* Data Grid */}
            <Card>
                <CardContent>
                    <Box sx={{ height: 600, width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            pagination
                            disableSelectionOnClick
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
